import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { AutoAwesome, Add } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

const TZ = (Intl.DateTimeFormat().resolvedOptions().timeZone) || 'America/Los_Angeles';

/**
 * "Today's usual classes": the classes the teacher usually holds on this weekday,
 * predicted by the backend from the last six weeks of attendance. One tap opens the
 * existing Mark Attendance screen with the class preselected; nothing is saved here.
 * Classes already marked for the day are hidden, the day panel below shows them.
 */
export default function UsualClasses({ date, doneClassIds = [] }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, items: [], weekday: dayjs(date).format('dddd'), error: false });

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: false }));
    api.getPredictions(date, TZ)
      .then(data => {
        if (cancelled) return;
        setState({ loading: false, items: data.items || [], weekday: data.weekday || dayjs(date).format('dddd'), error: false });
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) setState(s => ({ ...s, loading: false, items: [], error: true }));
      });
    return () => { cancelled = true; };
  }, [date]);

  const isToday = date === dayjs().format('YYYY-MM-DD');
  const title = isToday ? "Today's usual classes" : `Usual classes for ${dayjs(date).format('dddd, MMM D')}`;
  const pending = state.items.filter(i => !doneClassIds.includes(i.class_id));
  const allDone = state.items.length > 0 && pending.length === 0;

  let message = `No class usually on ${state.weekday}s.`;
  if (state.error) message = "Couldn't load your usual classes.";
  else if (allDone) message = isToday ? 'All caught up for today.' : 'All caught up for this day.';

  const manualButton = (
    <Button
      size="small" variant="outlined"
      onClick={() => navigate(`/attendance/${date}`)}
      sx={{
        flexShrink: 0, whiteSpace: 'nowrap', borderColor: '#93A3D8', color: '#5B6BA8', borderRadius: 2, fontSize: '0.8rem',
        '&:hover': { borderColor: '#6366f1', color: '#4f46e5', bgcolor: '#F8F9FF' }
      }}
    >
      Enter attendance manually
    </Button>
  );

  return (
    <Box sx={{
      mb: 2.5, border: '1px solid #EAECF0', borderRadius: 3, overflow: 'hidden',
      bgcolor: 'white', boxShadow: '0 1px 2px rgba(16,24,40,0.05)'
    }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesome sx={{ fontSize: 18, color: '#6366f1' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#101828' }}>{title}</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: '#667085', mt: 0.25 }}>
          Based on your last 6 weeks. Tap a class to mark attendance, then review and save.
        </Typography>
      </Box>

      {state.loading ? (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 2 }} />
        </Box>
      ) : pending.length > 0 ? (
        pending.map(item => (
          <Box
            key={item.class_id}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, px: 2, py: 1.25, borderTop: '1px solid #EAECF0' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.class_name}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#667085' }}>
                {item.confidence === 'usually' ? 'Usually' : 'Often'} on {state.weekday}s · held {item.held_count} of the last {item.window_weeks}
              </Typography>
            </Box>
            <Button
              size="small" variant="contained" startIcon={<Add />}
              onClick={() => navigate(`/attendance/${date}?classId=${item.class_id}`)}
              sx={{
                flexShrink: 0, whiteSpace: 'nowrap', bgcolor: '#6366f1', borderRadius: 2, fontSize: '0.8rem',
                boxShadow: '0 1px 2px rgba(16,24,40,0.05)', '&:hover': { bgcolor: '#4f46e5' }
              }}
            >
              Mark attendance
            </Button>
          </Box>
        ))
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.25, borderTop: '1px solid #EAECF0' }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#344054' }}>{message}</Typography>
          {manualButton}
        </Box>
      )}
    </Box>
  );
}
